import { AlertTriangle, Eye } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  ConfigurationTypeEnum,
  ContainerMaterialMatrixConfiguration,
  ContainerPeriodPricingConfiguration,
  TonnagePricingConfiguration,
  AutoDiscountConfiguration,
  DiscountConfiguration,
  PricingRuleFormData,
  DiscountTypeEnum,
} from '@libs/schemas';
import { LocationSelectionItem } from 'components/pricing/components/LocationSelector';
import { safeMoneyFromCents } from '@libs/services/money';
import { parse, format } from 'date-fns';

// Use PricingRuleFormData directly instead of a separate interface
type ReviewFormData = PricingRuleFormData;

interface ReviewStepProps {
  formData: ReviewFormData;
  selectedLocations: LocationSelectionItem[];
  customerIds: string[];
}

export function ReviewStep({ formData, selectedLocations, customerIds }: ReviewStepProps) {
  const formatScope = () => {
    const scope = formData.scope || {};
    const parts: string[] = [];

    if (scope.customerClassification && scope.customerClassification !== 'all') {
      parts.push(`Customer: ${scope.customerClassification}`);
    }

    if (selectedLocations.length > 0) {
      if (selectedLocations.length <= 3) {
        parts.push(`Locations: ${selectedLocations.map((loc) => loc.display).join(', ')}`);
      } else {
        parts.push(
          `Locations: ${selectedLocations
            .slice(0, 3)
            .map((loc) => loc.display)
            .join(', ')} and ${selectedLocations.length - 3} more`,
        );
      }
    }

    if (customerIds.length > 0) {
      parts.push(`Specific customers: ${customerIds.length} selected`);
    }

    return parts.length > 0 ? parts.join(', ') : 'No specific scope (applies to all)';
  };

  const formatConfigurationData = () => {
    const configData = formData.configuration?.data || {};

    switch (formData.configuration?.type) {
      case ConfigurationTypeEnum.TONNAGE_PRICING: {
        const tonnageData = configData as TonnagePricingConfiguration['data'];
        const rate = safeMoneyFromCents(tonnageData.perTonRateInCents || 0).toFormat();
        return `${rate} per ton (minimum ${tonnageData.minimumTonnage || 0} tons)`;
      }

      case ConfigurationTypeEnum.AUTO_DISCOUNT: {
        const discountData = configData as AutoDiscountConfiguration['data'];
        if (discountData.discountType === DiscountTypeEnum.PERCENTAGE) {
          return `${discountData.discountPercent || 0}% automatic discount`;
        } else {
          return `${safeMoneyFromCents(discountData.discountAmountInCents || 0).toFormat()} automatic discount`;
        }
      }

      case ConfigurationTypeEnum.DISCOUNT: {
        const discountData = configData as DiscountConfiguration['data'];
        if (discountData.discountType === DiscountTypeEnum.PERCENTAGE) {
          return `${discountData.discountPercent || 0}% discount`;
        } else {
          return `${safeMoneyFromCents(discountData.discountAmountInCents || 0).toFormat()} discount`;
        }
      }

      case ConfigurationTypeEnum.CONTAINER_MATERIAL_MATRIX: {
        const matrixData = configData as ContainerMaterialMatrixConfiguration['data'];
        const matrixLength = matrixData.matrixData ? Object.keys(matrixData.matrixData).length : 0;
        return `Single material matrix with ${matrixLength} combinations`;
      }

      case ConfigurationTypeEnum.CONTAINER_PERIOD_PRICING: {
        const periodData = configData as ContainerPeriodPricingConfiguration['data'];
        const containerCount = periodData.containerPeriods
          ? Object.keys(periodData.containerPeriods).length
          : 0;
        return `Flat rate package (${containerCount} container${containerCount !== 1 ? 's' : ''})`;
      }

      default:
        return 'Configuration not set';
    }
  };

  const getValidationIssues = () => {
    const issues: string[] = [];

    if (!formData.name) {
      issues.push('Rule name is required');
    }

    if (!formData.configuration?.type) {
      issues.push('Configuration type is required');
    }

    if (!formData.effectiveFrom) {
      issues.push('Effective from date is required');
    }

    if (!formData.configuration?.data || Object.keys(formData.configuration.data).length === 0) {
      issues.push('Configuration data is required');
    }

    return issues;
  };

  const getRuleStatus = (from?: string, to?: string) => {
    const now = new Date();
    // Parse date-only strings as local dates to avoid timezone shifts
    const fromDate = from ? parse(from, 'yyyy-MM-dd', new Date()) : undefined;
    const toDate = to ? parse(to, 'yyyy-MM-dd', new Date()) : undefined;
    if (fromDate && fromDate > now) return { status: 'pending', label: 'Pending' } as const;
    if (toDate && toDate < now) return { status: 'expired', label: 'Expired' } as const;
    return { status: 'active', label: 'Active' } as const;
  };

  const validationIssues = getValidationIssues();
  const isValid = validationIssues.length === 0;
  const ruleStatus = getRuleStatus(formData.effectiveFrom, formData.effectiveTo);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-semibold text-gray-900">Review & Confirm</h3>
        <p className="text-sm text-gray-600 mt-1">
          Review your pricing rule configuration before saving it.
        </p>
      </div>

      <Alert className="border-gray-200 bg-gray-50">
        <Eye className="h-4 w-4 text-gray-600" />
        <AlertDescription>
          <div className="space-y-3">
            <div className="text-gray-800 text-sm leading-relaxed">
              Review your pricing rule configuration below before saving.
            </div>
            <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
              <Badge
                variant={ruleStatus.status === 'active' ? 'default' : 'secondary'}
                className={`
                  ${ruleStatus.status === 'active' ? 'bg-green-100 text-green-800' : ''}
                  ${ruleStatus.status === 'pending' ? 'bg-blue-100 text-blue-800' : ''}
                  ${ruleStatus.status === 'expired' ? 'bg-red-100 text-red-800' : ''}
                `}
              >
                {ruleStatus.label}
              </Badge>
              {formData.precedence && formData.precedence !== 50 && (
                <Badge variant="outline" className="text-gray-600">
                  Priority: {formData.precedence}
                </Badge>
              )}
              {formData.canCombine && (
                <Badge variant="outline" className="text-gray-600">
                  Can combine with other rules
                </Badge>
              )}
            </div>
          </div>
        </AlertDescription>
      </Alert>

      {/* Validation Status */}
      {!isValid ? (
        <Card className={isValid ? 'border-green-200' : 'border-yellow-200'}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              {!isValid && (
                <>
                  <AlertTriangle className="h-5 w-5 text-yellow-600" />
                  <span className="text-yellow-700 font-medium">
                    Please fix the following issues:
                  </span>
                </>
              )}
            </div>
            {!isValid && (
              <ul className="mt-3 text-sm text-yellow-700 list-disc list-inside">
                {validationIssues.map((issue, index) => (
                  <li key={index}>{issue}</li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      ) : null}

      {/* Basic Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            Basic Information
            <Badge variant={formData.active ? 'success' : 'destructive'}>
              {formData.active ? 'Active' : 'Inactive'}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <span className="text-xs font-medium text-muted-foreground">Name:</span>
            <p className="font-medium text-sm">{formData.name}</p>
          </div>

          <div>
            <span className="text-xs font-medium text-muted-foreground">Type:</span>
            <div className="mt-1">
              {formData.configuration?.type ? (
                <Badge variant="outline">
                  {formData.configuration.type.replace(/_/g, ' ').toLowerCase()}
                </Badge>
              ) : (
                <span className="text-muted-foreground text-sm">Not set</span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="text-xs font-medium text-muted-foreground">Precedence:</span>
              <p className="text-sm">{formData.precedence}</p>
            </div>
            <div>
              <span className="text-xs font-medium text-muted-foreground">Can Combine:</span>
              <p className="text-sm">{formData.canCombine ? 'Yes' : 'No'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Configuration */}
      <Card>
        <CardHeader>
          <CardTitle>Configuration</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm">{formatConfigurationData()}</p>
        </CardContent>
      </Card>

      {/* Scope */}
      <Card>
        <CardHeader>
          <CardTitle>Scope</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm">{formatScope()}</p>
        </CardContent>
      </Card>

      {/* Timing */}
      <Card>
        <CardHeader>
          <CardTitle>Effective Dates</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div>
            <span className="text-xs font-medium text-muted-foreground">From:</span>
            <p className="text-sm">
              {formData.effectiveFrom
                ? format(parse(formData.effectiveFrom, 'yyyy-MM-dd', new Date()), 'MM/dd/yyyy')
                : 'Not set'}
            </p>
          </div>
          {formData.effectiveTo && (
            <div>
              <span className="text-xs font-medium text-muted-foreground">To:</span>
              <p className="text-sm">
                {format(parse(formData.effectiveTo, 'yyyy-MM-dd', new Date()), 'MM/dd/yyyy')}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
